import { endOfMonth, format, startOfMonth, subDays, subMonths } from 'date-fns'
import { useState } from 'react'
import { Helmet } from 'react-helmet'

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { Granularity, useEmailReport, useEmailReportSenders } from '@/models/emailReport'
import { EmailRateChart } from './EmailRateChart'
import { EmailTemplateBreakdown } from './EmailTemplateBreakdown'
import { ReportingControls } from './ReportingControls'
import { ReportingSummaryCards } from './ReportingSummaryCards'

const ISO = (d: Date) => format(d, 'yyyy-MM-dd')

export function EmailReportingIndex() {
  const [granularity, setGranularity] = useState<Granularity>(Granularity.DAY)
  const [start, setStart] = useState(ISO(subDays(new Date(), 29)))
  const [end, setEnd] = useState(ISO(new Date()))
  const [sender, setSender] = useState('')

  const { data: senders = [] } = useEmailReportSenders()
  const { data: report, isLoading } = useEmailReport({ granularity, start, end, sender: sender || undefined })

  const onGranularity = (g: Granularity) => {
    setGranularity(g)
    const now = new Date()
    if (g === Granularity.MONTH) {
      setStart(ISO(startOfMonth(subMonths(now, 11))))
      setEnd(ISO(endOfMonth(now)))
    } else {
      setStart(ISO(subDays(now, 29)))
      setEnd(ISO(now))
    }
  }

  return (
    <>
      <Helmet>
        <title>Email Reporting</title>
      </Helmet>
      <div className="flex flex-col gap-6 py-6 md:p-6">
        <h1 className="text-2xl font-semibold">Email Reporting</h1>
        <ReportingControls
          granularity={granularity}
          start={start}
          end={end}
          sender={sender}
          senders={senders}
          onGranularity={onGranularity}
          onStart={setStart}
          onEnd={setEnd}
          onSender={setSender}
        />
        {isLoading || !report ? (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-24 w-full" />
              ))}
            </div>
            <Skeleton className="h-64 w-full" />
            <SkeletonRows rows={4} />
          </div>
        ) : (
          <>
            <ReportingSummaryCards totals={report.totals} />
            <Card>
              <CardContent className="p-4">
                <EmailRateChart buckets={report.buckets} />
              </CardContent>
            </Card>
            <EmailTemplateBreakdown rows={report.byTemplate} />
          </>
        )}
      </div>
    </>
  )
}
